import type { Artifact } from './types';

export interface ArtifactGroups {
  screenshots: Artifact[];
  videos: Artifact[];
  traces: Artifact[];
  other: Artifact[];
}

const baseName = (name: string) => name.split('/').pop() ?? name;

/** A step trace page written by the e2e engine (trace.md). */
export const isTrace = (a: Artifact) => baseName(a.name).toLowerCase() === 'trace.md' || (a.contentType.startsWith('text/markdown') && /trace/i.test(a.name));

/** Sorts a result's artifacts into the sections shown in the report. Order within a group follows the name. */
export function groupArtifacts(artifacts: Artifact[] | null | undefined): ArtifactGroups {
  const groups: ArtifactGroups = { screenshots: [], videos: [], traces: [], other: [] };
  const sorted = (artifacts ?? []).slice().sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true }));
  for (const a of sorted) {
    if (a.contentType.startsWith('image/')) groups.screenshots.push(a);
    else if (a.contentType.startsWith('video/')) groups.videos.push(a);
    else if (isTrace(a)) groups.traces.push(a);
    else groups.other.push(a);
  }
  return groups;
}

/** Short label for an artifact: "attempt-2/screenshot-1.png" -> "screenshot-1.png (attempt 2)". */
export function artifactLabel(name: string): string {
  const parts = name.split('/');
  const file = parts.pop() ?? name;
  const attempt = parts.map((p) => /^attempt-(\d+)$/.exec(p)?.[1]).find(Boolean);
  return attempt ? `${file} (attempt ${attempt})` : file;
}

export function formatBytes(bytes: number | null | undefined): string {
  if (bytes === null || bytes === undefined || !Number.isFinite(bytes) || bytes < 0) return '';
  if (bytes < 1024) return `${bytes} B`;
  const units = ['KB', 'MB', 'GB'];
  let v = bytes / 1024;
  let i = 0;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i++;
  }
  return `${v >= 10 ? v.toFixed(0) : v.toFixed(1)} ${units[i]}`;
}
